import { comparisonFeatureDependencies } from 'data/constants';
import {
  AirHandlerProductType,
  ComparisonFeatureType,
  ComparisonTableDataType,
  CondenserProductType,
  SystemOptionType
} from 'data/types';
import { getDuctedCoverage } from 'utils/ducted-coverage';

const POSSIBLE_COMPARISON_FEATURES: ComparisonFeatureType[] = [
  'Price',
  'Potential Rebates',
  'Price After Rebates',
  'Heating Coverage',
  'Cooling Coverage',
  'Average Efficiency',
  'Total Units to Install',
  'Indoor Units',
  'Indoor Visibility',
  'Outdoor Units',
  'Outdoor Visibility',
  'Installation Difficulty',
  'Amperage'
];

export function buildComparisonRow(
  option: SystemOptionType,
  airHandlerProducts: AirHandlerProductType[],
  condenserProducts: CondenserProductType[]
): ComparisonTableDataType {
  return {
    identifier: option.uniqueId,
    price: option.price,
    heatingScore: option.heatingScore,
    coolingScore: option.coolingScore,
    condenserCoverage: option.condenserCoverage,
    ductedCoverage: getDuctedCoverage(option),
    averageSeer: option.averageSeer,
    totalUnitsToInstall:
      option.configuration.condensers.length +
      option.configuration.condensers.flatMap((cond) => cond.zones).length,
    totalIndoorUnits: option.configuration.condensers.flatMap(
      (cond) => cond.zones
    ).length,
    uniqueIndoorProducts: [
      ...new Map(
        option.configuration.condensers
          .flatMap((cond) => cond.zones)
          .map((zone) => zone.selectedProduct)
          .map((product) =>
            airHandlerProducts.find((p) => p.productId === product.productId)
          )
          .filter(
            (p): p is AirHandlerProductType => p !== undefined && p !== null
          )
          .map((obj) => [obj.productId, obj])
      ).values()
    ],
    totalOutdoorUnits: option.configuration.condensers.length,
    uniqueOutdoorProducts: [
      ...new Map(
        option.configuration.condensers
          .map((cond) =>
            condenserProducts.find(
              (p) => p.productId === cond.selectedProduct.productId
            )
          )
          .filter(
            (p): p is CondenserProductType => p !== undefined && p !== null
          )
          .map((obj) => [obj.productId, obj])
      ).values()
    ],
    totalRebates: option.totalRebates,
    indoorVisibility: option.indoorVisibility,
    outdoorVisibility: option.outdoorVisibility,
    installationDifficulty: option.installationDifficulty,
    amperage: option.amperage
  };
}

export function deriveGlobalComparisonFeatures(
  comparisonRows: ComparisonTableDataType[]
): ComparisonFeatureType[] {
  return POSSIBLE_COMPARISON_FEATURES.filter((feature) =>
    comparisonRows.some((row) =>
      comparisonFeatureDependencies[feature].every(
        (dep) => row[dep as keyof ComparisonTableDataType]
      )
    )
  );
}

export function resolveComparisonSystemName(
  system: SystemOptionType,
  index: number
): string {
  const curated = system.name?.trim();
  if (curated) return curated;
  return `System ${index + 1}`;
}
